import axios from "axios";
import { defaultSettings } from "contexts/appsettings/defaultSettings";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { REACT_APP_BACKENDURL } from "../../../config";
import { getSubscriptionStatusLoaderData } from "../comments/getSubscriptionStatusLoaderData";
import { eAccessRights } from "../enums";
import { HierarchyReadDto } from "./HierarchyReadDto";

/**
 * Loader data returned by the hierarchy loader, including subloads.
 */
export interface HierarchyLoaderData {
  hierarchy: HierarchyReadDto;
  parentHierarchies: HierarchyReadDto[];
  accessRights: eAccessRights[];
  isHierarchySubscribed: boolean;
}

/**
 * Data returned by the hierarchy loader API endpoint.
 */
export interface HierarchyLoaderReturn {
  hierarchy: HierarchyReadDto;
  parentHierarchies: HierarchyReadDto[];
  accessRights: eAccessRights[];
}

/**
 * Construct the hierarchy loader API URL from route params.
 *
 * @param params - Loader function params containing hierarchy identifiers
 * @returns The constructed URL for the hierarchy loader API endpoint
 */
export const getHierarchyLoaderUrl = (params: LoaderFunctionArgs["params"]): string => {
  const backEndUrl = REACT_APP_BACKENDURL.includes("_REACT_APP_BACKENDURL_")
    ? defaultSettings.backEndUrl
    : REACT_APP_BACKENDURL;

  const hierarchyTag =
    params.primaryHierarchyId !== undefined
      ? params.primaryHierarchyId
      : params.hierarchyTag !== undefined
        ? params.hierarchyTag
        : params.studyName !== undefined && params.reportName !== undefined
          ? `Report/${params.studyName}/${params.reportName}${params.tocMajorVersion ? `/${params.tocMajorVersion}` : ""}`
          : "Corporate";

  return `${backEndUrl}Hierarchy/Loader/${hierarchyTag}`;
};

/**
 * Fetch hierarchy loader data (non-loader version).
 *
 * @param params - Loader function params containing hierarchy identifiers
 * @returns The hierarchy loader data
 */
export const getHierarchyLoaderData = async (
  params: LoaderFunctionArgs["params"],
): Promise<HierarchyLoaderData> => {
  const hierarchyLoader = await axios.get(getHierarchyLoaderUrl(params));
  if (!hierarchyLoader || !hierarchyLoader.data) {
    throw new Error("Invalid response from HierarchyLoader, no data returned");
  }

  const subscriptionStatusLoader = await getSubscriptionStatusLoaderData({
    params: {
      entityType: "Hierarchy",
      entityId: hierarchyLoader.data.hierarchy.id as string,
    },
  });

  const ret = {
    hierarchy: hierarchyLoader.data.hierarchy,
    parentHierarchies: hierarchyLoader.data.parentHierarchies,
    accessRights: hierarchyLoader.data.accessRights,
    isHierarchySubscribed: subscriptionStatusLoader.isSubscribed,
  };
  return ret;
};

/**
 * Loader function that returns hierarchy loader data used by routes.
 *
 * @param props - Loader args provided by react-router
 * @param props.params - Route params used to identify the hierarchy
 */
export const hierarchyLoader: LoaderFunction = async ({ params }: LoaderFunctionArgs) =>
  getHierarchyLoaderData(params);
